import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { PostPurchaseDialogBuilder } from './post-purchase-dialog.builder';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { IRadioOption } from '../../../form-master/components/radio-field/radio-field.interface';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { LocalizationService } from '../../../localization/localization.service';
import { EmployeesRadioEnum } from '../../../settings/settings.data';
import { fromJS, List, Map } from 'immutable';
import { SettingsDataDispatcher } from '../../../settings-data/settings-data.dispatcher';
import {
  CompanyInfo,
  NumOfEmployeesEnum,
} from '../../../settings-data/sub-models/company-info.model';
import { PostPurchaseInfo } from '../../../settings-data/sub-models/post-purchase-info.model';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { DialogService } from '../../../dialog/dialog.service';
import { isEmpty } from 'lodash';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { HomeDataDispatcher } from '../../../home-data';
import { POST_PURCHASE_DIALOG_TYPE } from '../../../home-data/home-data.data';

@Component({
  selector: 'app-post-purchase-dialog',
  templateUrl: './post-purchase-dialog.component.html',
  styleUrls: ['./post-purchase-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [PostPurchaseDialogBuilder],
})
export class PostPurchaseDialogComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() title: string;
  @Input() header: string;
  @Input() companyInfo: CompanyInfo;
  @Input() postPurchaseInfo: PostPurchaseInfo;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _locale: string;

  EmployeesRadioEnum: typeof EmployeesRadioEnum;
  dialogType: string;
  formKeys: any;
  form: FormGroup;
  industryValues: List<ISelectOption>;
  employeesOptions: List<ISelectOption>;
  businessYears: List<IRadioOption>;
  whatMattersMostOptions: List<IRadioOption>;
  trackingObject: ITrackingObject;
  industryTapped: ITrackingAction;
  industrySelected: ITrackingAction;
  subIndustryTapped: ITrackingAction;
  subIndustrySelected: ITrackingAction;
  businessYearsTapped: ITrackingAction;
  employeesRadioTapped: ITrackingAction;
  employeesDropdownTapped: ITrackingAction;
  employeesDropdownSelected: ITrackingAction;
  priorityRadioSelected: ITrackingAction;
  doneTapped: ITrackingAction;
  continueLaterTapped: ITrackingAction;
  isFormNotCompleted$: Observable<boolean>;
  subIndustryValues$: BehaviorSubject<List<ISelectOption>>;
  trackingAdditionalProperties$: BehaviorSubject<any>;

  constructor(
    private _postPurchaseDialogBuilder: PostPurchaseDialogBuilder,
    private _settingsDataDispatcher: SettingsDataDispatcher,
    private _homeDataDispatcher: HomeDataDispatcher,
    private _localizationService: LocalizationService,
    private _dialogService: DialogService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.companyInfo || changes.postPurchaseInfo) &&
      this.companyInfo &&
      this.postPurchaseInfo
    ) {
      if (!this.form) {
        this.form = this._postPurchaseDialogBuilder.buildForm(
          this.companyInfo,
          this.postPurchaseInfo
        );
      } else {
        this.form.reset(
          this._postPurchaseDialogBuilder.buildFormGroupValues(
            this.companyInfo,
            this.postPurchaseInfo
          ),
          { emitEvent: false }
        );
      }
    }

    if (changes.trackingScreenName && this.trackingScreenName) {
      this.industryTapped = this._trackingActionBuilder.buildDropdownTapped(
        'industry',
        this.trackingScreenName
      );
      this.industrySelected = this._trackingActionBuilder.buildDropdownSelected(
        'industry',
        this.trackingScreenName
      );
      this.subIndustryTapped = this._trackingActionBuilder.buildDropdownTapped(
        'sub_industries',
        this.trackingScreenName
      );
      this.subIndustrySelected = this._trackingActionBuilder.buildDropdownSelected(
        'sub_industries',
        this.trackingScreenName
      );
      this.businessYearsTapped = this._trackingActionBuilder.buildRadioTapped(
        'years_business',
        this.trackingScreenName
      );
      this.employeesRadioTapped = this._trackingActionBuilder.buildRadioTapped(
        'employees',
        this.trackingScreenName
      );
      this.employeesDropdownTapped = this._trackingActionBuilder.buildDropdownTapped(
        'employees',
        this.trackingScreenName
      );
      this.employeesDropdownSelected = this._trackingActionBuilder.buildDropdownSelected(
        'employees',
        this.trackingScreenName
      );
      this.priorityRadioSelected = this._trackingActionBuilder.buildRadioTapped(
        'what_matters_most_to_you',
        this.trackingScreenName
      );
      this.doneTapped = this._trackingActionBuilder.buildButtonTapped(
        'done',
        this.trackingScreenName
      );
      this.continueLaterTapped = this._trackingActionBuilder.buildButtonTapped(
        'continue_later',
        this.trackingScreenName
      );
    }
  }

  ngOnInit() {
    this._locale = this._localizationService.getLocale();

    this.EmployeesRadioEnum = EmployeesRadioEnum;
    this.dialogType = POST_PURCHASE_DIALOG_TYPE;
    this.formKeys = this._postPurchaseDialogBuilder.FORM_KEYS;
    this.industryValues = this._postPurchaseDialogBuilder.buildIndustryValues(
      this._locale
    );
    this.businessYears = this._postPurchaseDialogBuilder.buildBusinessYears();
    this.employeesOptions = this._postPurchaseDialogBuilder.buildEmployeesOptions();
    this.whatMattersMostOptions = this._postPurchaseDialogBuilder.buildWhatMattersMostOptions();
    this.trackingObject = this._trackingObjectBuilder.buildSurvey(
      'post_purchase_personalization'
    );

    this.subIndustryValues$ = new BehaviorSubject<List<ISelectOption>>(void 0);
    this.trackingAdditionalProperties$ = new BehaviorSubject<any>(void 0);

    this.onSelectIndustry(
      this.industryField.value,
      this.subIndustryField.value
    );

    // update employees select field values when select employees radio field
    this.employeesRadioField.valueChanges
      .pipe(
        startWith(this.employeesRadioField.value),
        takeUntil(this._destroyed)
      )
      .subscribe((value) => {
        if (value !== EmployeesRadioEnum.there_are_more) {
          this.employeesField.setValue(NumOfEmployeesEnum._0, {
            emitEvent: false,
          });
          this.employeesField.disable({ emitEvent: false });
        } else {
          const numOfEmployees =
            (this.companyInfo && this.companyInfo.numOfEmployees) ||
            NumOfEmployeesEnum._1;

          this.employeesField.setValue(numOfEmployees, { emitEvent: false });
          this.employeesField.enable({ emitEvent: false });
        }
      });

    // updates isFormNotCompleted$ on form changes if dialog is opened
    this.isFormNotCompleted$ = this._dialogService.dialogTriggers$.pipe(
      filter(
        (dialogTrigger: IDialogTrigger) =>
          dialogTrigger.type === this.dialogType
      ),
      map(
        (dialogTrigger: IDialogTrigger) =>
          dialogTrigger.action === DialogActionsEnum.open
      ),
      distinctUntilChanged(),
      switchMap((isDialogOpen: boolean) => {
        if (isDialogOpen && this.form) {
          return this.form.valueChanges.pipe(
            startWith(this.form.value),
            map((values) =>
              fromJS(values).some((value) => isEmpty(value) || value.size === 0)
            )
          );
        }

        return of(true);
      })
    );
  }

  get industryField(): AbstractControl {
    return this.form.get(this.formKeys.INDUSTRY);
  }

  get subIndustryField(): AbstractControl {
    return this.form.get(this.formKeys.SUB_INDUSTRY);
  }

  get businessYearsField(): AbstractControl {
    return this.form.get(this.formKeys.BUSINESS_YEARS);
  }

  get employeesRadioField(): AbstractControl {
    return this.form.get(this.formKeys.EMPLOYEES_RADIO);
  }

  get employeesField(): AbstractControl {
    return this.form.get(this.formKeys.EMPLOYEES);
  }

  get whatMattersMostField(): AbstractControl {
    return this.form.get(this.formKeys.WHAT_MATTERS_MOST);
  }

  onConfirm() {
    const settingsContentProps = this._postPurchaseDialogBuilder.buildSettingsProps(
      fromJS(this.form.value),
      true
    );

    this._settingsDataDispatcher.updateSettings(settingsContentProps);
    this._settingsDataDispatcher.saveCurrentSettingsToNetwork();
  }

  onCancel() {
    const settingsContentProps = this._postPurchaseDialogBuilder.buildSettingsProps(
      fromJS(this.form.value),
      false
    );

    this._settingsDataDispatcher.updateSettings(settingsContentProps);
    this._settingsDataDispatcher.saveCurrentSettingsToNetwork();

    this._homeDataDispatcher.dismissPostPurchaseSurvey();
  }

  onSelectIndustry(chosenIndustry: string, chosenSubIndustry: string = '') {
    if (chosenIndustry) {
      this.subIndustryValues$.next(
        this._postPurchaseDialogBuilder.buildSubIndustryValues(
          this._locale,
          chosenIndustry
        )
      );

      this.subIndustryField.setValue(chosenSubIndustry, { emitEvent: false });
    }
  }

  updateTrackingAdditionalProperties(postPurchaseInfo: Map<string, any>) {
    this.trackingAdditionalProperties$.next(
      this._postPurchaseDialogBuilder.buildTrackingAdditionalProperties(
        postPurchaseInfo
      )
    );
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
